<template>
  <v-container fluid class="pa-0 admin-wrap">
    <!-- App Bar -->
    <v-app-bar flat dark class="grad-appbar">
      <v-toolbar-title style="color: white;" class="font-weight-bold">Admin Settings</v-toolbar-title>
      <v-spacer />
      <v-btn icon @click="refresh"><v-icon>mdi-refresh</v-icon></v-btn>
    </v-app-bar>

    <!-- HERO -->
    <div class="hero">
      <v-container>
        <v-row justify="center">
          <v-col cols="12" md="8">
            <v-card class="hero-card soft-card" rounded="xl">
              <v-row no-gutters align="center" class="pa-6">
                <v-col cols="12" md="3" class="text-center mb-4 mb-md-0">
                  <v-avatar size="110" class="ring">
                    <v-img :src="profile.image || defaultImage" />
                  </v-avatar>
                </v-col>

                <v-col cols="12" md="9" class="text-center text-md-left">
                  <div class="white--text text-h6 font-weight-bold">
                    {{ profile.name || 'Administrator' }}
                  </div>
                  <div class="white--text opacity-80">{{ profile.email || '—' }}</div>

                  <!-- Quick Actions -->
                  <div class="mt-4 d-flex flex-wrap">
                    <v-btn text small rounded class="chip-btn mr-2 mb-2" @click="$router.push({name:'AdminUsers'})">
                      <v-icon left small>mdi-account-multiple-outline</v-icon> Manage Users
                    </v-btn>
                    <v-btn text small rounded class="chip-btn mr-2 mb-2" @click="$router.push({name:'AdminGroups'})">
                      <v-icon left small>mdi-account-group</v-icon> View Groups
                    </v-btn>
                    <v-btn text small rounded class="chip-btn mr-2 mb-2" @click="$router.push({name:'AdminCalendarView'})">
                      <v-icon left small>mdi-calendar</v-icon> Calendar
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

        <!-- Soft bubbles -->
        <div class="hero-bubble a"></div>
        <div class="hero-bubble b"></div>
      </v-container>
    </div>

    <!-- CONTENT -->
    <v-container class="content-area py-8">
      <v-row justify="center">
        <v-col cols="12" md="8">
          <!-- Tiny stats -->
          <v-row class="mb-5">
            <v-col cols="12" sm="4">
              <v-card class="stat-card soft-card" rounded="xl">
                <div class="d-flex align-center">
                  <v-avatar size="36" class="stat-avatar teal"><v-icon color="white">mdi-account</v-icon></v-avatar>
                  <div class="ml-3">
                    <div class="stat-value">{{ stats.users }}</div>
                    <div class="stat-label">Users</div>
                  </div>
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="4">
              <v-card class="stat-card soft-card" rounded="xl">
                <div class="d-flex align-center">
                  <v-avatar size="36" class="stat-avatar amber"><v-icon color="white">mdi-account-group</v-icon></v-avatar>
                  <div class="ml-3">
                    <div class="stat-value">{{ stats.groups }}</div>
                    <div class="stat-label">Groups</div>
                  </div>
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="4">
              <v-card class="stat-card soft-card" rounded="xl">
                <div class="d-flex align-center">
                  <v-avatar size="36" class="stat-avatar red"><v-icon color="white">mdi-alert-circle-outline</v-icon></v-avatar>
                  <div class="ml-3">
                    <div class="stat-value">{{ stats.flags }}</div>
                    <div class="stat-label">Flagged Posts</div>
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Settings list -->
          <v-skeleton-loader
            v-if="loading"
            type="list-item-two-line, list-item, list-item, list-item, list-item, actions"
            class="rounded-xl"
          />
          <v-list v-else two-line class="settings soft-card" rounded>
            <v-subheader class="white--text">Account</v-subheader>

            <v-list-item class="hoverable" @click="$router.push({name:'Profile'})">
              <v-list-item-avatar tile><v-icon color="white">mdi-account</v-icon></v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="white--text">Profile</v-list-item-title>
                <v-list-item-subtitle class="white--text opacity-80">Photo, bio</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-icon><v-icon color="white">mdi-chevron-right</v-icon></v-list-item-icon>
            </v-list-item>

            <v-list-item class="hoverable" @click="$router.push({name:'PersonalInformationView'})">
              <v-list-item-avatar tile><v-icon color="white">mdi-card-account-details-outline</v-icon></v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="white--text">Personal Information</v-list-item-title>
                <v-list-item-subtitle class="white--text opacity-80">Name, email, phone</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-icon><v-icon color="white">mdi-chevron-right</v-icon></v-list-item-icon>
            </v-list-item>

            <v-subheader class="white--text mt-4">Administration</v-subheader>

            <v-list-item class="hoverable" @click="$router.push({name:'AdminUsers'})">
              <v-list-item-avatar tile><v-icon color="white">mdi-account-multiple-outline</v-icon></v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="white--text">Manage Users</v-list-item-title>
                <v-list-item-subtitle class="white--text opacity-80">Search, edit roles, suspend</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-icon><v-icon color="white">mdi-chevron-right</v-icon></v-list-item-icon>
            </v-list-item>

            <v-list-item class="hoverable" @click="$router.push({name:'AdminGroups'})">
              <v-list-item-avatar tile><v-icon color="white">mdi-account-group</v-icon></v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="white--text">View Groups</v-list-item-title>
                <v-list-item-subtitle class="white--text opacity-80">Create, edit, remove</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-icon><v-icon color="white">mdi-chevron-right</v-icon></v-list-item-icon>
            </v-list-item>

            <v-list-item class="hoverable" @click="$router.push({name:'AdminWaitlist'})">
              <v-list-item-avatar tile><v-icon color="white">mdi-file</v-icon></v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="white--text">View Waitlist</v-list-item-title>
                <v-list-item-subtitle class="white--text opacity-80">Create, edit, remove</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-icon><v-icon color="white">mdi-chevron-right</v-icon></v-list-item-icon>
            </v-list-item>

            <v-subheader class="white--text mt-4">Security</v-subheader>

            <v-list-item class="hoverable" @click="$router.push({name:'SecuritySettingsView'})">
              <v-list-item-avatar tile><v-icon color="white">mdi-shield-lock-outline</v-icon></v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="white--text">Security Settings</v-list-item-title>
                <v-list-item-subtitle class="white--text opacity-80">Password</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-icon><v-icon color="white">mdi-chevron-right</v-icon></v-list-item-icon>
            </v-list-item>

            <v-divider class="my-2" />

            <v-list-item class="hoverable" @click="logout">
              <v-list-item-avatar tile><v-icon color="error">mdi-logout</v-icon></v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="white--text">Logout</v-list-item-title>
              </v-list-item-content>
              <v-list-item-icon><v-icon color="white">mdi-chevron-right</v-icon></v-list-item-icon>
            </v-list-item>
          </v-list>
        </v-col>
      </v-row>
    </v-container>

    <!-- Bottom Nav -->
    <v-bottom-navigation app style="background: linear-gradient(to right, #4B60C6, #000000);" grow dark>
      <v-btn icon to="/home">
        <span>
          <v-icon>mdi-home</v-icon>
          <p class="tab-label">Home</p>
        </span>
      </v-btn>
      <v-btn icon to="/search">
        <span><v-icon>mdi-magnify</v-icon><p class="tab-label">Search</p></span>
      </v-btn>
      <v-btn icon to="/explore">
        <span><v-icon>mdi-compass-outline</v-icon><p class="tab-label">Explore</p></span>
      </v-btn>
      <v-btn icon to="/messages">
        <span><v-icon>mdi-message-text-outline</v-icon><p class="tab-label">Messages</p></span>
      </v-btn>
      <v-btn icon to="/groups">
        <span><v-icon>mdi-account-group</v-icon><p class="tab-label">Groups</p></span>
      </v-btn>
      <v-btn icon @click="$goToProfile()">
        <span><v-icon>mdi-account</v-icon><p class="tab-label">Profile</p></span>
      </v-btn>
    </v-bottom-navigation>

    <v-snackbar v-model="snack.show" :timeout="2500" :color="snack.color">{{ snack.text }}</v-snackbar>
  </v-container>
</template>

<script>
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import { db, auth } from "@/firebase";
import { signOut } from "firebase/auth";

export default {
  name: "AdminSettingsView",
  data() {
    return {
      loading: true,
      profile: { image: "", name: "", email: "" },
      stats: { users: 0, groups: 0, flags: 0 },
      defaultImage: require("@/assets/logo.png"),
      darkMode: false,
      snack: { show: false, text: "", color: "info" },
    };
  },
  methods: {
    toast(text, color = "info") { this.snack = { show: true, text, color }; },
    async fetchProfile() {
      const uid = auth.currentUser?.uid;
      if (!uid) return;
      const snap = await getDoc(doc(db, "user", uid));
      if (snap.exists()) {
        const d = snap.data();
        this.profile.name = d.fullName || "Administrator";
        this.profile.image = d.image || "";
        this.profile.email = d.email || "";
      }
    },
    async fetchStats() {
      // lightweight counts (client-side)
      try {
        const users = await getDocs(collection(db, "user"));
        const groups = await getDocs(collection(db, "groups"));
        // If you store flags somewhere else, adjust here; defaulting to 0.
        this.stats = { users: users.size, groups: groups.size, flags: 0 };
      } catch (e) {
        console.error(e);
      }
    },
    applyDarkMode() {
      if (this.$vuetify?.theme) this.$vuetify.theme.dark = this.darkMode;
    },
    async logout() {
      try {
        await signOut(auth);
        this.$router.push("/");
      } catch (e) {
        console.error(e);
        this.toast("Logout failed. Try again.", "error");
      }
    },
    refresh() {
      this.loading = true;
      Promise.all([this.fetchProfile(), this.fetchStats()])
        .then(() => this.toast("Refreshed", "success"))
        .finally(() => (this.loading = false));
    },
    goProfile() {
      this.$router.push({ name: "MemberSettings", params: { id: auth.currentUser?.uid } });
    },
  },
  async mounted() {
    this.darkMode = !!this.$vuetify?.theme?.dark;
    await Promise.all([this.fetchProfile(), this.fetchStats()]);
    this.loading = false;
  },
};
</script>

<style scoped>
.admin-wrap { min-height: 100vh; background: linear-gradient(180deg, #4B60C6 0%, #000000 100%); }

.grad-appbar { background: linear-gradient(135deg, #4B60C6, #000000); }

/* Hero */
.hero { position: relative; padding: 24px 0 8px; }
.hero-card { background: linear-gradient(135deg, #4B60C6, #000000); }
.hero-bubble { position:absolute; border-radius:50%; background: rgba(255,255,255,.10); filter: blur(2px); pointer-events:none; }
.hero-bubble.a { width: 260px; height: 260px; right: -80px; top: -80px; }
.hero-bubble.b { width: 160px; height: 160px; left: -60px; bottom: -60px; }
.ring { box-shadow: 0 0 0 4px rgba(255,255,255,.25), 0 10px 20px rgba(0,0,0,.22); background:white; }

/* Content */
.content-area { padding-bottom: 96px; margin-top: -8px; }
.soft-card { box-shadow: 0 12px 26px rgba(0,0,0,.14); border-radius: 16px; }

/* Stats */
.stat-card { background: rgba(255,255,255,.12); color: white; }
.stat-avatar { display:flex; align-items:center; justify-content:center; border-radius: 12px; }
.stat-avatar.teal { background: rgba(0,150,136,.9); }
.stat-avatar.amber { background: rgba(255,193,7,.95); }
.stat-avatar.red { background: rgba(244,67,54,.95); }
.stat-value { font-size: 1.1rem; font-weight: 800; line-height: 1; color: white; }
.stat-label { font-size: .8rem; opacity: .85; color: white; }

/* List */
.settings { background: linear-gradient(135deg, #4B60C6, #000000); }
.hoverable { transition: background-color .2s ease; cursor: pointer; }
.hoverable:hover { background-color: rgba(255,255,255,.10) !important; }
.white--text { color: white !important; }
.opacity-80 { opacity: .8; }

/* Quick chips */
.chip-btn { background: rgba(255,255,255,.18); color:white; text-transform:none; font-weight:700; }
.chip-btn:hover { background: rgba(255,255,255,.24); }

.tab-label { font-size: 10px; margin-top: 2px; color: white; }
</style>